/* eslint-disable jsdoc/require-jsdoc, @typescript-eslint/no-explicit-any -- 手写后端 JSON 边界在 OpenAPI 重新生成前做运行时归一化。 */
import { httpClient } from "../../../lib/http/axios";
import { normalizeHttpError } from "../../../lib/http/errors";
import type {
  FulfillmentOrderDetail,
  FulfillmentOrderArchive,
  FulfillmentOrderChangeBatch,
  FulfillmentOrderContractBaseline,
  FulfillmentOrderItineraryDay,
  FulfillmentOrderLine,
  FulfillmentOrderResourceSelection,
  ApplyFulfillmentOrderChangeRequest,
  FulfillmentOrderListResult,
  FulfillmentWorkspaceEditor,
  FulfillmentWorkspace,
  FulfillmentItineraryPoint,
  FulfillmentBudgetImportReport,
} from "../types";

export type CourseRouteDay = { dayNo: number; date: string | null; stayName?: string | null };

const CHANGE_REQUEST_TIMEOUT_MS = 120_000;
const WORKSPACE_SAVE_TIMEOUT_MS = 120_000;

/** 增减项使用同一幂等键重放一次，收口“数据库已提交但首个响应超时”的不确定状态。 */
async function postIdempotentChange(
  url: string,
  payload: Record<string, unknown>,
): Promise<Envelope<Record<string, any>>> {
  const config = { timeout: CHANGE_REQUEST_TIMEOUT_MS, "axios-retry": { retries: 0 } };
  try {
    return (await httpClient.post<Envelope<Record<string, any>>>(url, payload, config)).data;
  } catch (error) {
    if (!normalizeHttpError(error).retriable) throw error;
    return (await httpClient.post<Envelope<Record<string, any>>>(url, payload, config)).data;
  }
}

/** 工作区是完整快照最后写入者生效；响应超时后重放同一快照，优先确认最终入库结果。 */
async function putLastWriteWinsWorkspace(
  url: string,
  payload: Record<string, unknown>,
): Promise<Envelope<Record<string, any>>> {
  const config = { timeout: WORKSPACE_SAVE_TIMEOUT_MS, "axios-retry": { retries: 0 } };
  try {
    return (await httpClient.put<Envelope<Record<string, any>>>(url, payload, config)).data;
  } catch (error) {
    if (!normalizeHttpError(error).retriable) throw error;
    return (await httpClient.put<Envelope<Record<string, any>>>(url, payload, config)).data;
  }
}

export type CourseRouteLine = {
  id: string;
  workspaceId: string;
  fulfillmentOrderId: string | null;
  lineNo: number;
  logicalLineId: string;
  originType: "version_base" | "post_finalization_add";
  introducedAfterVersionId: string | null;
  originChangeBatchId: string | null;
  incorporatedVersionId: string | null;
  resourceType: string;
  transportMode?: "rail" | "air" | "ship" | null;
  spuTableName: string | null;
  spuId: string | null;
  skuTableName: string | null;
  skuId: string | null;
  resourceSelection: FulfillmentOrderResourceSelection | null;
  itemName: string;
  itemSpec: string | null;
  fulfillmentSupplierId: string | null;
  fulfillmentSupplierName: string | null;
  procurementChannelType:
    | "direct_resource_provider"
    | "ground_operator"
    | "other_intermediary"
    | "unknown";
  procurementSourceType: string | null;
  procurementSourceId: string | null;
  providerAssignmentStatus: "not_applicable" | "unassigned" | "assigned";
  settlementSupplierId: string | null;
  settlementSupplierName: string | null;
  dayExpression: string;
  dayNumbers: number[];
  serviceDates: string[];
  dateMappingStatus: "mapped" | "needs_adjustment";
  travelerRole: string | null;
  mealType: string | null;
  pricingUnitType: string;
  quantity: string;
  billingCount: string;
  unit: string;
  unitPrice: string;
  amount: string;
  reviewStatus: "pending" | "confirmed";
  sourceType: "manual" | "history";
  status: string;
  remark: string | null;
};

export type CourseRouteAgreement = {
  id: string;
  orderNo: string;
  workspaceId: string;
  settlementSupplierId: string;
  settlementSupplierName: string;
  supplierContactName: string | null;
  supplierContactPhone: string | null;
  companyPartyName: string;
  documentRevision: number;
  baseVersionId: string | null;
  baseVersionNo: number | null;
  baseAmount: string | null;
  currentAmount: string;
  status: "draft" | "effective";
  effectiveAt: string | null;
  archiveObjectKey: string | null;
  archiveFileName: string | null;
  archiveVersion: number | null;
  baselineAmount: string | null;
  deltaAmount: string;
  hasContractDifference: boolean;
  changeCount: number;
  updatedAt: string;
};

export type CourseRouteAgreementDetail = {
  itineraryId: string;
  itineraryTitle: string;
  serviceDateFrom: string | null;
  serviceDateTo: string | null;
  companyContactName: string | null;
  companyContactPhone: string | null;
  itineraryDays: FulfillmentOrderItineraryDay[];
  agreement: CourseRouteAgreement;
  lines: CourseRouteLine[];
  baseline: FulfillmentOrderContractBaseline | null;
  archives: FulfillmentOrderArchive[];
  changes: FulfillmentOrderChangeBatch[];
};

export type CourseRouteWorkspaceEditor = {
  budgetImport?: FulfillmentBudgetImportReport | null;
  workspace: {
    id: string;
    itineraryId: string;
    projectId: string | null;
    customerQuoteId: string | null;
    budgetOrderId: string | null;
    itineraryTitle: string;
    serviceDateFrom: string | null;
    serviceDateTo: string | null;
    routeDays: CourseRouteDay[];
    itineraryPoints?: FulfillmentItineraryPoint[];
    peopleCounts?: FulfillmentWorkspace["peopleCounts"];
    revision: number;
    currentVersionId: string | null;
    currentVersionNo: number;
    lineNoHighWater: number;
    status: string;
    updatedAt: string;
  };
  lines: CourseRouteLine[];
  agreements: CourseRouteAgreement[];
};

export type CourseRouteHistoryItem = {
  workspaceId: string;
  itineraryId: string;
  itineraryTitle: string;
  serviceDateFrom: string | null;
  serviceDateTo: string | null;
  lineCount: number;
  totalAmount: string;
  updatedAt: string;
};

export type CourseRouteWorkspaceListQuery = {
  page: number;
  pageSize: number;
  q?: string;
  status?: string;
  supplierId?: string;
  itineraryId?: string;
  sortField: string;
  sortDirection: string;
};

type Envelope<T> = { code: string; message?: string; data: T };

function assertOk<T>(response: Envelope<T>): T {
  if (response.code !== "OK") throw new Error(response.message || "确认件请求失败");
  return response.data;
}

/** 把兼容编辑器中的修订号转换为后端 CAS 整数。 */
function parseWorkspaceRevision(revision: string): number {
  const value = Number(revision);
  if (!Number.isInteger(value) || value < 1) {
    throw new Error("确认件修订号无效，请刷新页面后重试");
  }
  return value;
}

function mapLine(raw: Record<string, any>): CourseRouteLine {
  return {
    id: String(raw.id),
    workspaceId: String(raw.workspace_id),
    fulfillmentOrderId: raw.fulfillment_order_id ?? null,
    lineNo: Number(raw.line_no),
    logicalLineId: String(raw.logical_line_id ?? raw.id),
    originType: raw.origin_type === "post_finalization_add"
      ? "post_finalization_add"
      : "version_base",
    introducedAfterVersionId: raw.introduced_after_version_id ?? null,
    originChangeBatchId: raw.origin_change_batch_id ?? null,
    incorporatedVersionId: raw.incorporated_version_id ?? null,
    resourceType: String(raw.resource_type),
    transportMode: raw.transport_mode === "rail" || raw.transport_mode === "air" || raw.transport_mode === "ship" ? raw.transport_mode : null,
    spuTableName: raw.spu_table_name ?? null,
    spuId: raw.spu_id ?? null,
    skuTableName: raw.sku_table_name ?? null,
    skuId: raw.sku_id ?? null,
    resourceSelection: mapResourceSelection(raw.resource_selection),
    itemName: String(raw.item_name_snapshot),
    itemSpec: raw.item_spec_snapshot ?? null,
    fulfillmentSupplierId: raw.fulfillment_supplier_id ?? null,
    fulfillmentSupplierName: raw.fulfillment_supplier_name_snapshot ?? null,
    procurementChannelType: raw.procurement_channel_type ?? "unknown",
    procurementSourceType: raw.procurement_source_type ?? null,
    procurementSourceId: raw.procurement_source_id ?? null,
    providerAssignmentStatus: raw.provider_assignment_status ?? "not_applicable",
    settlementSupplierId: raw.settlement_supplier_id ?? null,
    settlementSupplierName: raw.settlement_supplier_name_snapshot ?? null,
    dayExpression: String(raw.day_expression),
    dayNumbers: (raw.day_numbers ?? []).map(Number),
    serviceDates: (raw.service_dates ?? []).map(String),
    dateMappingStatus: raw.date_mapping_status,
    travelerRole: raw.traveler_role ?? null,
    mealType: raw.meal_type ?? null,
    pricingUnitType: String(raw.pricing_unit_type),
    quantity: String(raw.quantity),
    billingCount: String(raw.billing_count),
    unit: String(raw.unit),
    unitPrice: String(raw.unit_price_amount),
    amount: String(raw.amount),
    reviewStatus: raw.review_status,
    sourceType: raw.source_type,
    status: String(raw.status),
    remark: raw.remark ?? null,
  };
}

function mapResourceSelection(raw: Record<string, any> | null | undefined): FulfillmentOrderResourceSelection | null {
  if (raw?.kind === "transport" && ["rail", "air"].includes(raw.transport_mode)
    && raw.from_point_id && raw.to_point_id) {
    return { kind: "transport", transportMode: raw.transport_mode,
      fromPointId: String(raw.from_point_id), toPointId: String(raw.to_point_id) };
  }
  if (!raw) return null;
  if (raw.kind === "stay" && raw.stay_spu_id && raw.sku_name) {
    return {
      kind: "stay",
      staySpuId: String(raw.stay_spu_id),
      skuName: String(raw.sku_name),
    };
  }
  if (raw.kind === "bus" && raw.vehicle_spec_name
    && ["pickup", "dropoff", "local_charter", "transfer"].includes(raw.entry_semantic)) {
    return {
      kind: "bus",
      vehicleSpecName: String(raw.vehicle_spec_name),
      entrySemantic: raw.entry_semantic,
    };
  }
  if (raw.kind === "point" && raw.point_spu_id && raw.sku_name) {
    return {
      kind: "point",
      pointSpuId: String(raw.point_spu_id),
      skuName: String(raw.sku_name),
    };
  }
  return null;
}

function mapResourceSelectionInput(selection: FulfillmentOrderResourceSelection | null) {
  if (selection?.kind === "transport") {
    return { kind: "transport" as const, transport_mode: selection.transportMode,
      from_point_id: selection.fromPointId, to_point_id: selection.toPointId };
  }
  return selection?.kind === "stay"
    ? {
        kind: "stay" as const,
        stay_spu_id: selection.staySpuId,
        sku_name: selection.skuName,
      }
    : selection?.kind === "bus"
      ? {
          kind: "bus" as const,
          vehicle_spec_name: selection.vehicleSpecName,
          entry_semantic: selection.entrySemantic,
        }
      : selection?.kind === "point"
        ? {
            kind: "point" as const,
            point_spu_id: selection.pointSpuId,
            sku_name: selection.skuName,
          }
      : null;
}

function mapLineNoHighWater(raw: unknown, lines: CourseRouteLine[]): number {
  const activeLineNoHighWater = lines.reduce(
    (highest, line) => line.status === "active" ? Math.max(highest, line.lineNo) : highest,
    0,
  );
  return typeof raw === "number"
    && Number.isSafeInteger(raw)
    && raw >= activeLineNoHighWater
    ? raw
    : activeLineNoHighWater;
}

function mapAgreement(raw: Record<string, any>): CourseRouteAgreement {
  return {
    id: String(raw.id),
    orderNo: String(raw.order_no),
    workspaceId: String(raw.workspace_id),
    settlementSupplierId: String(raw.settlement_supplier_id),
    settlementSupplierName: String(raw.settlement_supplier_name),
    supplierContactName: raw.supplier_contact_name ?? null,
    supplierContactPhone: raw.supplier_contact_phone ?? null,
    companyPartyName: String(raw.company_party_name),
    documentRevision: Number(raw.document_revision),
    baseVersionId: raw.base_version_id ?? null,
    baseVersionNo: raw.base_version_no == null ? null : Number(raw.base_version_no),
    baseAmount: raw.base_amount == null ? null : String(raw.base_amount),
    currentAmount: String(raw.current_amount),
    status: raw.status,
    effectiveAt: raw.effective_at ?? null,
    archiveObjectKey: raw.archive_object_key ?? null,
    archiveFileName: raw.archive_file_name ?? null,
    archiveVersion: raw.archive_version == null ? null : Number(raw.archive_version),
    baselineAmount: raw.baseline_amount == null ? null : String(raw.baseline_amount),
    deltaAmount: String(raw.delta_amount ?? "0.00"),
    hasContractDifference: Boolean(raw.has_contract_difference),
    changeCount: Number(raw.change_count ?? 0),
    updatedAt: String(raw.updated_at),
  };
}

function mapContractBaseline(raw: Record<string, any> | null | undefined): FulfillmentOrderContractBaseline | null {
  if (!raw) return null;
  return {
    id: String(raw.id),
    documentRevision: Number(raw.document_revision),
    baselineRevision: Number(raw.baseline_revision),
    baselineAmount: String(raw.baseline_amount),
    contentHash: String(raw.content_hash),
    backfillStatus: String(raw.backfill_status),
    signedAt: String(raw.signed_at),
    firstArchiveId: raw.first_archive_id ?? null,
    snapshot: raw.snapshot ?? {},
    lines: (raw.lines ?? []).map((line: Record<string, any>) => ({
      sourceLineId: String(line.source_line_id),
      lineNo: Number(line.line_no),
      amount: String(line.amount),
      snapshot: line.snapshot ?? {},
    })),
  };
}

function mapArchive(raw: Record<string, any>, agreementId: string): FulfillmentOrderArchive {
  return {
    id: String(raw.id),
    fulfillmentOrderId: agreementId,
    versionId: String(raw.document_revision),
    archiveVersion: Number(raw.archive_version),
    documentRevision: Number(raw.document_revision),
    originalFileName: String(raw.file_name),
    mimeType: String(raw.mime_type),
    fileSize: Number(raw.file_size),
    fileSha256: String(raw.file_sha256),
    status: String(raw.status),
    replacedById: raw.replaced_by_id ?? null,
    archivedAt: String(raw.archived_at),
    downloadUrl: raw.download_url ? String(raw.download_url) : undefined,
  };
}

function mapChangeBatch(raw: Record<string, any>): FulfillmentOrderChangeBatch {
  return {
    id: String(raw.id),
    orderId: raw.order_id ?? null,
    baseVersionId: raw.base_version_id ?? null,
    changeType: raw.change_type ?? "legacy_change",
    expectedRevision: Number(raw.expected_revision),
    revisionAfter: Number(raw.revision_after),
    idempotencyKey: String(raw.idempotency_key),
    reason: String(raw.reason),
    amountBefore: String(raw.amount_before),
    amountAfter: String(raw.amount_after),
    deltaAmount: String(raw.delta_amount),
    hasFulfillmentSupplierChange: Boolean(raw.has_fulfillment_supplier_change),
    hasSettlementSupplierChange: Boolean(raw.has_settlement_supplier_change),
    actorId: raw.actor_id == null ? null : Number(raw.actor_id),
    createdAt: String(raw.created_at),
    items: (raw.items ?? []).map((item: Record<string, any>) => ({
      id: String(item.id),
      sequenceNo: Number(item.sequence_no),
      action: item.action,
      sourceLineId: item.source_line_id ?? null,
      replacementLineId: item.replacement_line_id ?? null,
      baseVersionLineId: item.base_version_line_id ?? null,
      amount: String(item.amount),
      snapshot: item.snapshot ?? {},
    })),
  };
}

/** 查询课程线路确认件列表，不再依赖核价或协议版本字段。 */
export async function listCourseRouteWorkspaces(
  query: CourseRouteWorkspaceListQuery,
): Promise<FulfillmentOrderListResult> {
  const response = await httpClient.get<Envelope<Record<string, any>>>("/fulfillment-orders", {
    params: {
      page: query.page,
      page_size: query.pageSize,
      ...(query.q ? { q: query.q } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.supplierId ? { settlement_supplier_id: query.supplierId } : {}),
      ...(query.itineraryId ? { itinerary_id: query.itineraryId } : {}),
      sort_field: query.sortField,
      sort_direction: query.sortDirection,
    },
  });
  const raw = assertOk(response.data);
  return {
    items: (raw.items ?? []).map((item: Record<string, any>) => ({
      id: String(item.id),
      orderNo: String(item.order_no),
      workspaceId: String(item.workspace_id),
      sourceBudgetOrderId: "",
      sourceBudgetOrderNo: "",
      itineraryId: String(item.itinerary_id),
      itineraryTitle: String(item.itinerary_title),
      settlementSupplierId: String(item.settlement_supplier_id ?? ""),
      settlementSupplierName: String(item.settlement_supplier_name_snapshot ?? ""),
      currentVersionId: null,
      currentVersionNo: null,
      currentAmount: String(item.current_amount),
      status: String(item.status),
      unassignedLineCount: Number(item.unassigned_line_count),
      orderCount: Number(item.order_count),
      createdAt: String(item.created_at),
      updatedAt: String(item.updated_at),
    })),
    total: Number(raw.total ?? 0),
    page: Number(raw.page ?? query.page),
    pageSize: Number(raw.page_size ?? query.pageSize),
  };
}

function courseRouteLineToLegacy(line: CourseRouteLine): FulfillmentOrderLine {
  return {
    id: line.id,
    workspaceId: line.workspaceId,
    fulfillmentOrderId: line.fulfillmentOrderId,
    sourceBudgetLineId: null,
    sourceType: line.sourceType,
    lineNo: line.lineNo,
    logicalLineId: line.logicalLineId,
    originType: line.originType,
    introducedAfterVersionId: line.introducedAfterVersionId,
    originChangeBatchId: line.originChangeBatchId,
    incorporatedVersionId: line.incorporatedVersionId,
    resourceType: line.resourceType,
    transportMode: line.transportMode,
    spuTableName: line.spuTableName,
    spuId: line.spuId,
    skuTableName: line.skuTableName,
    skuId: line.skuId,
    resourceSelection: line.resourceSelection,
    itemNameSnapshot: line.itemName,
    itemSpecSnapshot: line.itemSpec,
    fulfillmentSupplierId: line.fulfillmentSupplierId,
    fulfillmentSupplierName: line.fulfillmentSupplierName,
    procurementChannelType: line.procurementChannelType,
    procurementSourceType: line.procurementSourceType,
    procurementSourceId: line.procurementSourceId,
    providerAssignmentStatus: line.providerAssignmentStatus ?? "not_applicable",
    settlementSupplierId: line.settlementSupplierId,
    settlementSupplierName: line.settlementSupplierName,
    serviceDateFrom: line.serviceDates.at(0) ?? null,
    serviceDateTo: line.serviceDates.at(-1) ?? null,
    dayExpression: line.dayExpression,
    dayNumbers: line.dayNumbers,
    serviceDates: line.serviceDates,
    dateMappingStatus: line.dateMappingStatus,
    reviewStatus: line.reviewStatus,
    travelerRole: line.travelerRole,
    mealType: line.mealType,
    pricingUnitType: line.pricingUnitType,
    quantity: line.quantity,
    billingCount: line.billingCount,
    unit: line.unit,
    unitPriceAmount: line.unitPrice,
    amount: line.amount,
    sourceBudgetAmount: null,
    diffAmount: null,
    diffReason: null,
    status: line.status,
    remark: line.remark,
  };
}

function mapEditor(raw: Record<string, any>): CourseRouteWorkspaceEditor {
  const lines = (raw.lines ?? []).map(mapLine);
  return {
    budgetImport: raw.budget_import ?? null,
    workspace: {
      id: String(raw.workspace.id),
      itineraryId: String(raw.workspace.itinerary_id),
      projectId: raw.workspace.project_id ?? null,
      customerQuoteId: raw.workspace.customer_quote_id ?? null,
      budgetOrderId: raw.workspace.budget_order_id ?? null,
      itineraryTitle: String(raw.workspace.itinerary_title),
      serviceDateFrom: raw.workspace.service_date_from ?? null,
      serviceDateTo: raw.workspace.service_date_to ?? null,
      peopleCounts: raw.workspace.people_counts ? {
        studentCount: raw.workspace.people_counts.student_count ?? null,
        teacherCount: raw.workspace.people_counts.teacher_count ?? null,
        guideCount: raw.workspace.people_counts.guide_count ?? null,
        driverCount: raw.workspace.people_counts.driver_count ?? null,
      } : null,
      routeDays: (raw.workspace.route_days ?? []).map((item: Record<string, any>) => ({
        dayNo: Number(item.day_no),
        date: item.date ?? null,
        stayName: item.stay_name ?? null,
      })),
      itineraryPoints: (raw.workspace.itinerary_points ?? []).map((item: Record<string, any>) => ({
        nodeId: String(item.node_id),
        pointSpuId: String(item.point_spu_id),
        pointName: String(item.point_name),
        dayNo: Number(item.day_no),
        timeSlot: String(item.time_slot),
        sortOrder: Number(item.sort_order),
      })),
      revision: Number(raw.workspace.revision),
      currentVersionId: raw.workspace.current_version_id ?? null,
      currentVersionNo: Number(raw.workspace.current_version_no ?? 0),
      lineNoHighWater: mapLineNoHighWater(raw.workspace.line_no_high_water, lines),
      status: String(raw.workspace.status),
      updatedAt: String(raw.workspace.updated_at),
    },
    lines,
    agreements: (raw.agreements ?? []).map(mapAgreement),
  };
}

function mapLineInput(line: CourseRouteLine) {
  return {
    id: line.id.startsWith("local-") ? null : line.id,
    line_no: line.lineNo,
    resource_type: line.resourceType,
    spu_table_name: line.spuTableName,
    spu_id: line.spuId,
    sku_table_name: line.skuTableName,
    sku_id: line.skuId,
    resource_selection: mapResourceSelectionInput(line.resourceSelection),
    item_name_snapshot: line.itemName,
    item_spec_snapshot: line.itemSpec,
    fulfillment_supplier_id: line.fulfillmentSupplierId,
    fulfillment_supplier_name_snapshot: line.fulfillmentSupplierName,
    procurement_channel_type: line.procurementChannelType,
    procurement_source_type: line.procurementSourceType,
    procurement_source_id: line.procurementSourceId,
    provider_assignment_status: line.providerAssignmentStatus ?? "not_applicable",
    settlement_supplier_id: line.settlementSupplierId,
    settlement_supplier_name_snapshot: line.settlementSupplierName,
    day_expression: line.dayExpression,
    day_numbers: line.dayNumbers,
    service_dates: line.serviceDates,
    traveler_role: line.travelerRole,
    meal_type: line.mealType,
    pricing_unit_type: line.pricingUnitType,
    quantity: line.quantity,
    billing_count: line.billingCount,
    unit: line.unit,
    unit_price_amount: line.unitPrice,
    review_status: line.reviewStatus,
    source_type: line.sourceType,
    remark: line.remark,
  };
}

export async function fetchCourseRouteWorkspace(
  workspaceId: string,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.get<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/current`,
  );
  return mapEditor(assertOk(response.data));
}

export async function saveCourseRouteWorkspace(
  editor: CourseRouteWorkspaceEditor,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await putLastWriteWinsWorkspace(
    `/fulfillment-orders/workspaces/${editor.workspace.id}/current`,
    { revision: editor.workspace.revision, lines: editor.lines.map(mapLineInput) },
  );
  return mapEditor(assertOk(response));
}

/** 将课程线路确认件转换为现有编辑器外壳，废弃来源字段统一为空。 */
export function toLegacyFulfillmentEditor(
  editor: CourseRouteWorkspaceEditor,
): FulfillmentWorkspaceEditor {
  const sourceLinks = new Map(editor.budgetImport?.line_links?.map(link => [link.line_id, link]) ?? []);
  const lines = editor.lines.map(line => {
    const mapped = courseRouteLineToLegacy(line);
    const source = sourceLinks.get(line.id);
    return source ? { ...mapped, sourceBudgetLineId: source.source_line_id, sourceBudgetAmount: source.source_amount } : mapped;
  });
  return {
    workspace: {
      budgetImport: editor.budgetImport ?? null,
      id: editor.workspace.id,
      sourceBudgetOrderId: editor.budgetImport?.budget_order_id ?? "",
      sourceBudgetOrderNo: "",
      sourceBudgetVersionNo: editor.budgetImport?.version_no ?? 0,
      sourceBudgetUpdatedAt: editor.budgetImport?.source_updated_at ?? "",
      sourceBudgetTotalAmount: editor.budgetImport?.source_total_amount ?? "0",
      itineraryId: editor.workspace.itineraryId,
      projectId: editor.workspace.projectId,
      customerQuoteId: editor.workspace.customerQuoteId,
      itineraryTitle: editor.workspace.itineraryTitle,
      serviceDateFrom: editor.workspace.serviceDateFrom ?? "",
      serviceDateTo: editor.workspace.serviceDateTo ?? "",
      status: editor.workspace.status,
      revision: String(editor.workspace.revision),
      currentVersionId: editor.workspace.currentVersionId,
      currentVersionNo: editor.workspace.currentVersionNo,
      lineNoHighWater: editor.workspace.lineNoHighWater,
      routeDays: editor.workspace.routeDays,
      itineraryPoints: editor.workspace.itineraryPoints ?? [],
      peopleCounts: editor.workspace.peopleCounts ?? null,
    },
    lines,
    orders: editor.agreements.map((agreement) => ({
      id: agreement.id,
      orderNo: agreement.orderNo,
      workspaceId: editor.workspace.id,
      sourceBudgetOrderId: "",
      itineraryId: editor.workspace.itineraryId,
      settlementSupplierId: agreement.settlementSupplierId,
      settlementSupplierName: agreement.settlementSupplierName,
      supplierContactName: agreement.supplierContactName,
      supplierContactPhone: agreement.supplierContactPhone,
      currentVersionId: agreement.baseVersionId,
      currentVersionNo: agreement.baseVersionNo,
      documentRevision: agreement.documentRevision,
      baselineAmount: agreement.baseAmount ?? agreement.baselineAmount ?? agreement.currentAmount,
      currentAmount: agreement.currentAmount,
      status: agreement.status,
      updatedAt: agreement.updatedAt,
    })),
    sourceDiff: {
      workspaceId: editor.workspace.id,
      sourceBudgetTotalAmount: "0",
      currentTotalAmount: lines.reduce(
        (sum, line) => sum + Number(line.amount),
        0,
      ).toFixed(2),
      diffTotalAmount: "0",
      manualLineCount: lines.length,
      manualAmount: lines.reduce((sum, line) => sum + Number(line.amount), 0).toFixed(2),
      unassignedLineCount: lines.filter((line) => !line.fulfillmentOrderId).length,
      hasChanges: false,
      lines: [],
    },
    quickEntryContext: null,
    quickEntryContextError: null,
  };
}

function legacyLineToCourseRoute(line: FulfillmentOrderLine): CourseRouteLine {
  return {
    id: line.id,
    workspaceId: line.workspaceId,
    fulfillmentOrderId: line.fulfillmentOrderId,
    lineNo: line.lineNo,
    logicalLineId: line.logicalLineId ?? line.id,
    originType: line.originType ?? "version_base",
    introducedAfterVersionId: line.introducedAfterVersionId ?? null,
    originChangeBatchId: line.originChangeBatchId ?? null,
    incorporatedVersionId: line.incorporatedVersionId ?? null,
    resourceType: line.resourceType,
    spuTableName: line.spuTableName ?? null,
    spuId: line.spuId ?? null,
    skuTableName: line.skuTableName ?? null,
    skuId: line.skuId ?? null,
    resourceSelection: line.resourceSelection ?? null,
    itemName: line.itemNameSnapshot,
    itemSpec: line.itemSpecSnapshot ?? null,
    fulfillmentSupplierId: line.fulfillmentSupplierId ?? null,
    fulfillmentSupplierName: line.fulfillmentSupplierName ?? null,
    procurementChannelType: line.procurementChannelType ?? "unknown",
    procurementSourceType: line.procurementSourceType ?? null,
    procurementSourceId: line.procurementSourceId ?? null,
    providerAssignmentStatus: line.providerAssignmentStatus ?? "not_applicable",
    settlementSupplierId: line.settlementSupplierId ?? null,
    settlementSupplierName: line.settlementSupplierName ?? null,
    dayExpression: line.dayExpression ?? "D1",
    dayNumbers: line.dayNumbers ?? [1],
    serviceDates: line.serviceDates ?? [line.serviceDateFrom].filter(Boolean) as string[],
    dateMappingStatus: line.dateMappingStatus ?? "mapped",
    travelerRole: line.travelerRole ?? null,
    mealType: line.mealType ?? null,
    pricingUnitType: line.pricingUnitType,
    quantity: line.quantity,
    billingCount: line.billingCount,
    unit: line.unit,
    unitPrice: line.unitPriceAmount,
    amount: line.amount,
    reviewStatus: line.reviewStatus ?? "confirmed",
    sourceType: line.sourceType === "history" ? "history" : "manual",
    status: line.status,
    remark: line.remark ?? null,
  };
}

export async function saveLegacyCourseRouteWorkspace(
  workspaceId: string,
  revision: string,
  lines: FulfillmentOrderLine[],
): Promise<FulfillmentWorkspaceEditor> {
  const editor = await saveCourseRouteWorkspace({
    workspace: {
      id: workspaceId,
      itineraryId: "",
      projectId: null,
      customerQuoteId: null,
      budgetOrderId: null,
      itineraryTitle: "",
      serviceDateFrom: null,
      serviceDateTo: null,
      routeDays: [],
      revision: parseWorkspaceRevision(revision),
      currentVersionId: null,
      currentVersionNo: 0,
      lineNoHighWater: lines.reduce(
        (highest, line) => line.status === "active" ? Math.max(highest, line.lineNo) : highest,
        0,
      ),
      status: "draft",
      updatedAt: "",
    },
    lines: lines.map(legacyLineToCourseRoute),
    agreements: [],
  });
  return toLegacyFulfillmentEditor(editor);
}

export async function generateLegacyCourseRouteAgreement(
  workspaceId: string,
  revision: string,
  lines: FulfillmentOrderLine[],
  selectedLineNos: number[],
  settlementSupplierId: string,
  replaceDraftAgreement = false,
): Promise<FulfillmentWorkspaceEditor> {
  const generated = await generateCourseRouteAgreement(
    workspaceId,
    parseWorkspaceRevision(revision),
    [],
    settlementSupplierId,
    lines.map(legacyLineToCourseRoute),
    selectedLineNos,
    replaceDraftAgreement,
  );
  return toLegacyFulfillmentEditor(generated);
}

export async function searchCourseRouteHistory(
  query: string,
  excludeItineraryId: string,
): Promise<CourseRouteHistoryItem[]> {
  const response = await httpClient.get<Envelope<Record<string, any>[]>>(
    "/fulfillment-orders/history",
    { params: { q: query.trim(), exclude_itinerary_id: excludeItineraryId, limit: 20 } },
  );
  return assertOk(response.data).map((raw) => ({
    workspaceId: String(raw.workspace_id),
    itineraryId: String(raw.itinerary_id),
    itineraryTitle: String(raw.itinerary_title),
    serviceDateFrom: raw.service_date_from ?? null,
    serviceDateTo: raw.service_date_to ?? null,
    lineCount: Number(raw.line_count),
    totalAmount: String(raw.total_amount),
    updatedAt: String(raw.updated_at),
  }));
}

export async function replaceCourseRouteFromHistory(
  workspaceId: string,
  sourceWorkspaceId: string,
  revision: number,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/history/${sourceWorkspaceId}/replace`,
    { revision },
    { "axios-retry": { retries: 0 } },
  );
  return mapEditor(assertOk(response.data));
}

export async function confirmCourseRouteLines(
  workspaceId: string,
  revision: number,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/lines/confirm-all`,
    { revision },
    { "axios-retry": { retries: 0 } },
  );
  return mapEditor(assertOk(response.data));
}

/** 将确认件切换为定稿后的增减项编辑模式。 */
export async function finalizeCourseRouteWorkspace(
  workspaceId: string,
  revision: number,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/finalize`,
    { revision },
    { timeout: 30_000, "axios-retry": { retries: 0 } },
  );
  return mapEditor(assertOk(response.data));
}

/** 取消确认件定稿并恢复直接编辑模式。 */
export async function unfinalizeCourseRouteWorkspace(
  workspaceId: string,
  revision: number,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/unfinalize`,
    { revision },
    { timeout: 30_000, "axios-retry": { retries: 0 } },
  );
  return mapEditor(assertOk(response.data));
}

/** 读取确认件工作区全部增减项，不依赖供应商协议。 */
export async function listCourseRouteWorkspaceChanges(
  workspaceId: string,
): Promise<FulfillmentOrderChangeBatch[]> {
  const response = await httpClient.get<Envelope<Record<string, any>[]>>(
    `/fulfillment-orders/workspaces/${workspaceId}/changes`,
  );
  return assertOk(response.data).map(mapChangeBatch);
}

/** 对已定稿确认件的未归口明细追加增减项。 */
export async function applyFinalizedCourseRouteWorkspaceChange(
  workspaceId: string,
  request: ApplyFulfillmentOrderChangeRequest,
): Promise<FulfillmentOrderChangeBatch> {
  const response = await postIdempotentChange(
    `/fulfillment-orders/workspaces/${workspaceId}/changes`,
    {
      expected_revision: request.expectedRevision,
      idempotency_key: request.idempotencyKey,
      reason: request.reason.trim(),

      adjustment: request.adjustment ? {
        source_line_id: request.adjustment.sourceLineId,
        quantity: request.adjustment.quantity,
        billing_count: request.adjustment.billingCount,
        spec_unit: request.adjustment.specUnit,
        unit_price_amount: request.adjustment.unitPriceAmount,
      } : null,
      add_lines: request.addLines.map((line) => ({
        ...mapLineInput(legacyLineToCourseRoute(line)),
        id: null,
      })),
    },
  );
  return mapChangeBatch(assertOk(response));
}

export async function generateCourseRouteAgreement(
  workspaceId: string,
  revision: number,
  lineIds: string[],
  settlementSupplierId: string,
  lines?: CourseRouteLine[],
  selectedLineNos?: number[],
  replaceDraftAgreement = false,
): Promise<CourseRouteWorkspaceEditor> {
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/workspaces/${workspaceId}/agreements`,
    {
      revision,
      line_ids: lineIds,
      ...(lines ? { lines: lines.map(mapLineInput) } : {}),
      ...(selectedLineNos ? { selected_line_nos: selectedLineNos } : {}),
      settlement_supplier_id: settlementSupplierId,
      ...(replaceDraftAgreement ? { replace_draft_agreement: true } : {}),
    },
    { "axios-retry": { retries: 0 } },
  );
  return mapEditor(assertOk(response.data));
}

export async function uploadCourseRouteAgreement(
  agreementId: string,
  documentRevision: number,
  file: File,
): Promise<CourseRouteAgreement> {
  const form = new FormData();
  form.append("file", file);
  const response = await httpClient.post<Envelope<Record<string, any>>>(
    `/fulfillment-orders/${agreementId}/archive-current`,
    form,
    { params: { document_revision: documentRevision }, "axios-retry": { retries: 0 } },
  );
  return mapAgreement(assertOk(response.data));
}

/** 应用签后增减或添加录入；新增明细不携带原行主键。 */
export async function applyLegacyCourseRouteSigningChange(
  agreementId: string,
  request: ApplyFulfillmentOrderChangeRequest,
): Promise<FulfillmentOrderChangeBatch> {
  const normalizedReason = request.reason.trim();
  if (!normalizedReason) throw new Error("请填写增减说明");
  const response = await postIdempotentChange(
    `/fulfillment-orders/${agreementId}/changes`,
    {
      expected_revision: request.expectedRevision,
      idempotency_key: request.idempotencyKey,
      reason: normalizedReason,

      adjustment: request.adjustment ? {
        source_line_id: request.adjustment.sourceLineId,
        quantity: request.adjustment.quantity,
        billing_count: request.adjustment.billingCount,
        spec_unit: request.adjustment.specUnit,
        unit_price_amount: request.adjustment.unitPriceAmount,
      } : null,
      add_lines: request.addLines.map((line) => ({
        ...mapLineInput(legacyLineToCourseRoute(line)),
        id: null,
      })),
    },
  );
  return mapChangeBatch(assertOk(response));
}

/** 读取单个结算供应商的当前协议，不暴露历史版本。 */
export async function fetchCourseRouteAgreementDetail(
  agreementId: string,
): Promise<CourseRouteAgreementDetail> {
  const response = await httpClient.get<Envelope<Record<string, any>>>(
    `/fulfillment-orders/${agreementId}/current`,
  );
  const raw = assertOk(response.data);
  const agreement = mapAgreement(raw.agreement);
  return {
    itineraryId: String(raw.itinerary_id),
    itineraryTitle: String(raw.itinerary_title),
    serviceDateFrom: raw.service_date_from ?? null,
    serviceDateTo: raw.service_date_to ?? null,
    companyContactName: raw.company_contact_name ?? null,
    companyContactPhone: raw.company_contact_phone ?? null,
    itineraryDays: (raw.itinerary_days ?? []).map((day: Record<string, any>) => ({
      dayNo: Number(day.day_no),
      date: day.date ?? null,
      locationSummary: String(day.location_summary ?? ""),
      stayName: day.stay_name ?? null,
      routeSummary: String(day.route_summary ?? ""),
    })),
    agreement,
    lines: (raw.lines ?? []).map(mapLine),
    baseline: mapContractBaseline(raw.baseline),
    archives: (raw.archives ?? []).map((item: Record<string, any>) => mapArchive(item, agreement.id)),
    changes: (raw.changes ?? []).map(mapChangeBatch),
  };
}

/** 将当前协议适配到既有文书组件所需的只读外壳。 */
export function toLegacyFulfillmentOrderDetail(
  detail: CourseRouteAgreementDetail,
): FulfillmentOrderDetail {
  const { agreement } = detail;
  const versionId = agreement.baseVersionId ?? String(agreement.documentRevision);
  const versionNo = agreement.baseVersionNo ?? agreement.documentRevision;
  return {
    itineraryId: detail.itineraryId,
    companyPartyName: agreement.companyPartyName,
    companyContactName: detail.companyContactName,
    companyContactPhone: detail.companyContactPhone,
    itineraryDays: detail.itineraryDays,
    order: {
      id: agreement.id,
      orderNo: agreement.orderNo,
      workspaceId: agreement.workspaceId,
      sourceBudgetOrderId: "",
      itineraryId: detail.itineraryId,
      settlementSupplierId: agreement.settlementSupplierId,
      settlementSupplierName: agreement.settlementSupplierName,
      supplierContactName: agreement.supplierContactName,
      supplierContactPhone: agreement.supplierContactPhone,
      currentVersionId: versionId,
      currentVersionNo: versionNo,
      documentRevision: agreement.documentRevision,
      baselineAmount: agreement.baseAmount
        ?? detail.baseline?.baselineAmount
        ?? agreement.baselineAmount
        ?? agreement.currentAmount,
      currentAmount: agreement.currentAmount,
      status: agreement.status,
      updatedAt: agreement.updatedAt,
    },
    currentVersion: {
      id: versionId,
      fulfillmentOrderId: agreement.id,
      versionNo,
      previousVersionId: null,
      versionType: "current",
      status: "active",
      settlementSupplierId: agreement.settlementSupplierId,
      settlementSupplierName: agreement.settlementSupplierName,
      amount: agreement.currentAmount,
      changeSummary: null,
      archiveCount: detail.archives.length || (agreement.archiveObjectKey ? 1 : 0),
      createdAt: agreement.updatedAt,
    },
    versions: [],
    lines: detail.lines.map(courseRouteLineToLegacy),
    baseline: detail.baseline,
    archives: detail.archives,
    changes: detail.changes,
  };
}
